import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import _ from 'lodash'
import { IDoesFilterPassParams } from 'ag-grid-community'
import { CustomFilterProps, useGridFilter } from 'ag-grid-react'
import { APIResponse } from '../../../../../../lib/commons/api'
import { BulkSheetFilter, FilterFooter, FilterInput } from '../common'
import {
  SelectFilter,
  SelectFilterFormControl,
  SelectFilterArea,
  CheckboxArea,
  FilterLabel,
} from '../SelectFilter'
import { Checkbox, Typography } from '@mui/material'
import { ICON_SIZE } from '../../../../BulkSheet/const'
import { useSelector } from 'react-redux'
import { AllState } from '../../../../../../store'
import usePrevious from '../../../../../hooks/usePrevious'

type Props = CustomFilterProps & {
  optionKey: string
  fetchOptions?: (params: any) => Promise<object[]>
  getValue: (option: any) => string
  getLabel: (option: any) => string
  fetch: (v: SelectFilter) => Promise<APIResponse>
  getValuesForOptionFilter?: (context: any) => Promise<string>
  sortValues?: (context: any) => any
}

const buildSelectFilterModel = (
  values: string[],
  includeBlank: boolean,
  allOptionValues: string[]
): SelectFilter | null => {
  const isAllSelected =
    allOptionValues.length > 0 &&
    values.length === allOptionValues.length &&
    includeBlank
  return isAllSelected ? null : { values, includeBlank }
}

export const ServerSideSelectFilter = (params: Props) => {
  const {
    model,
    onModelChange,
    api,
    optionKey,
    fetchOptions,
    getValue,
    getLabel,
    fetch: fetchFn,
    getValuesForOptionFilter,
    context,
    sortValues,
  } = params
  const [options, setOptions] = useState<any[]>([])
  const refreshOptions = useCallback(async () => {
    let allOptions = context[optionKey] ?? []
    if (!optionKey && fetchOptions) {
      allOptions = await fetchOptions(params)
    }
    const optionFilterValues =
      getValuesForOptionFilter && (await getValuesForOptionFilter(context))
    const optionsInternal = optionFilterValues
      ? allOptions.filter((option: any) =>
          optionFilterValues.includes(getValue(option))
        )
      : allOptions
    if (sortValues) sortValues(optionsInternal)
    setOptions(optionsInternal)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時と保存後のみ実行。context/params は毎レンダーで新規参照になるため deps に入れない
  }, [])
  // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時のみ実行。refreshOptions は useCallback で安定
  useEffect(() => {
    refreshOptions()
  }, [])
  const hasRequiredSaveData = useSelector<AllState>(
    state => state.hasRequiredSaveData.hasRequiredSaveData
  )
  const prevHasRequiredSaveData = usePrevious(hasRequiredSaveData)

  // Derive state from model (single source of truth)
  const allOptionValues = options.map(getValue)
  const values = (model as SelectFilter | null)?.values ?? allOptionValues
  const includeBlank = (model as SelectFilter | null)?.includeBlank ?? true
  const isAllSelected =
    !model || (values.length === allOptionValues.length && includeBlank)
  const isAllDeselected = model !== null && values.length === 0 && !includeBlank

  // UI-local state
  const [text, setText] = useState('')
  const [loading, isLoading] = useState(false)
  const [filteredIds, setFilteredIds] = useState<Set<string> | undefined>(
    undefined
  )

  const addedRowIds = useRef<string[]>()
  useEffect(() => {
    if (addedRowIds.current) return
    addedRowIds.current = []
  }, [])

  const fetchFilteredIds = async (v: SelectFilter) => {
    const response = await fetchFn(v)
    setFilteredIds(new Set(response.json))
  }

  const search = useMemo(
    () =>
      _.debounce(async (v: SelectFilter | null) => {
        if (!v) {
          setFilteredIds(undefined)
          return
        }
        if (v.values.length === 0 && !v.includeBlank) {
          setFilteredIds(new Set())
          return
        }
        try {
          isLoading(true)
          await fetchFilteredIds(v)
        } finally {
          isLoading(false)
        }
      }, 300),
    []
  )

  // When criteria changes (model changes), trigger debounced search
  const modelKey = JSON.stringify(model)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- modelKey は model の値比較用。model 自体は毎レンダーで新規オブジェクトになるため deps に入れると毎回発火する。search は useMemo で安定
  useEffect(() => {
    search(model as SelectFilter | null)
  }, [modelKey])

  // When filteredIds changes, trigger re-filtering
  // eslint-disable-next-line react-hooks/exhaustive-deps -- api は AG Grid から渡される安定参照。filteredIds 変更時のみ再フィルタリングをトリガーする
  useEffect(() => {
    api.onFilterChanged()
  }, [filteredIds])

  // Re-fetch after save
  // eslint-disable-next-line react-hooks/exhaustive-deps -- hasRequiredSaveData の変化のみをトリガーとし、その時点の最新 model/fetchFilteredIds を読む
  useEffect(() => {
    const isAfterSavedOrRefreshed =
      prevHasRequiredSaveData && !hasRequiredSaveData
    isAfterSavedOrRefreshed && refreshOptions()
    if (isAfterSavedOrRefreshed && model) {
      fetchFilteredIds(model as SelectFilter).then(() => {
        addedRowIds.current = []
      })
    }
  }, [hasRequiredSaveData])

  useGridFilter({
    doesFilterPass(params: IDoesFilterPassParams) {
      // Don't filter new rows
      if (params.data.added) {
        if (addedRowIds.current && params.node.id) {
          addedRowIds.current.push(params.node.id)
        }
        return true
      }
      if (
        addedRowIds.current &&
        params.node.id &&
        addedRowIds.current.includes(params.node.id)
      ) {
        return true
      }
      if (!filteredIds) return true
      return params.node.id ? filteredIds.has(params.node.id) : false
    },

    getModelAsString() {
      if (!model) return ''
      return `${includeBlank ? '(Blank),' : ''}${options
        .filter(option => values.includes(getValue(option)))
        .map(option => getLabel(option))
        .join(',')}`
    },
  })

  // Helpers for model updates
  const updateValues = (newValues: string[]) => {
    onModelChange(
      buildSelectFilterModel(newValues, includeBlank, allOptionValues)
    )
  }

  const updateIncludeBlank = (newIncludeBlank: boolean) => {
    onModelChange(
      buildSelectFilterModel(values, newIncludeBlank, allOptionValues)
    )
  }

  return (
    <BulkSheetFilter>
      <FilterInput
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder={'Search...'}
        sx={{ height: 22 }}
      />
      <SelectFilterArea>
        <SelectFilterFormControl
          label="(Select All)"
          control={
            <Checkbox
              checked={isAllSelected}
              indeterminate={!isAllSelected && !isAllDeselected}
              onChange={e =>
                e.target.checked
                  ? onModelChange(null)
                  : onModelChange({ values: [], includeBlank: false })
              }
              disableRipple={true}
            />
          }
        />
        <CheckboxArea>
          {!text && (
            <SelectFilterFormControl
              label="(Blanks)"
              control={
                <Checkbox
                  checked={includeBlank}
                  onChange={e => updateIncludeBlank(e.target.checked)}
                  disableRipple={true}
                />
              }
            />
          )}
          {options
            .filter(option => !text || getLabel(option).includes(text))
            .map(option => {
              const value = getValue(option)
              return (
                <SelectFilterFormControl
                  key={`select-filter-${value}`}
                  label={
                    <FilterLabel
                      sx={{
                        backgroundColor: option.backgroundColor ?? 'inherit',
                      }}
                    >
                      {option.iconUrl && (
                        <img
                          src={option.iconUrl}
                          style={{
                            height: `${ICON_SIZE}px`,
                            width: `${ICON_SIZE}px`,
                            borderRadius: '50%',
                          }}
                        />
                      )}
                      <Typography sx={{ padding: '0 3px' }}>
                        {getLabel(option)}
                      </Typography>
                    </FilterLabel>
                  }
                  control={
                    <Checkbox
                      checked={values.includes(value)}
                      onChange={e => {
                        if (e.target.checked) {
                          updateValues([...values, value])
                        } else {
                          updateValues(values.filter(v => v !== value))
                        }
                      }}
                      disableRipple={true}
                    />
                  }
                />
              )
            })}
        </CheckboxArea>
      </SelectFilterArea>
      <FilterFooter loading={loading} onClick={() => onModelChange(null)} />
    </BulkSheetFilter>
  )
}
